import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PackageCheck, FileDown, ShieldCheck, PenLine, Truck, Handshake } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useEstablishment } from "@/context/EstablishmentContext";

export default function Restitutions() {
  const { t } = useLang();
  const { scope, selectedId } = useEstablishment();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const STATUS_LABEL = { restituted: t("res.stRestituted"), shipped: t("st.shipped"), delivered: t("st.delivered") };

  const methodLabel = (r) => {
    if (r.restitution_method === "shipping") return t("res.stShipped");
    if (r.restitution_method === "in_person") return t("res.stRestituted");
    return STATUS_LABEL[r.status] || r.status;
  };
  const isShipping = (r) => r.restitution_method === "shipping" || (!r.restitution_method && (r.status === "shipped" || r.status === "delivered"));

  useEffect(() => {
    (async () => {
      try { setData(await api(scope("/claim/restitutions"))); }
      catch (e) { toast.error(e.message); setData({ restitutions: [], count: 0 }); }
    })();
  }, [scope, selectedId]);

  async function downloadProof(r) {
    setDownloading(r.item_id);
    try {
      const res = await fetch(`${API}/claim/${r.declaration_id}/restitution-receipt.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || t("res.error").replace("{status}", res.status)); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `findloop-preuve-restitution-${r.declaration_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setDownloading(null); }
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <PageHeader title={t("res.title")} subtitle={t("res.subtitle")} testid="restitutions-header" />

      {data === null ? (
        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden divide-y divide-border" data-testid="restitutions-skeleton">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-2/3 rounded bg-slate-100 animate-pulse" />
                <div className="h-2.5 w-1/3 rounded bg-slate-100 animate-pulse" />
              </div>
              <div className="h-6 w-24 rounded-full bg-slate-100 animate-pulse" />
              <div className="h-8 w-24 rounded-full bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
      ) : data.count === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="restitutions-empty">
          <PackageCheck size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{t("res.empty")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden divide-y divide-border" data-testid="restitutions-list">
          {data.restitutions.map((r) => (
            <div key={r.item_id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`restitution-row-${r.item_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{r.object}</p>
                <p className="text-xs text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
                  <span>{r.customer_name}</span>
                  {r.restituted_at && <span>· {String(r.restituted_at).slice(0, 10)}</span>}
                </p>
              </div>
              <Badge className="bg-slate-100 text-slate-600 flex items-center gap-1" data-testid={`restitution-method-${r.item_id}`}>
                {isShipping(r) ? <Truck size={12} /> : <Handshake size={12} />}
                {methodLabel(r)}
              </Badge>
              {r.code_verified && (
                <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid={`restitution-code-${r.item_id}`}><ShieldCheck size={12} /> {t("res.codeValidated")}</Badge>
              )}
              {r.signed && (
                <Badge className="bg-indigo-100 text-findo-indigo flex items-center gap-1" data-testid={`restitution-signed-${r.item_id}`}><PenLine size={12} /> {t("res.signed")}</Badge>
              )}
              <Button size="sm" variant="outline" onClick={() => downloadProof(r)} disabled={downloading === r.item_id || !r.has_proof}
                className="rounded-full h-8 text-xs font-semibold" data-testid={`restitution-proof-${r.item_id}`}>
                <FileDown size={13} className="mr-1" /> {downloading === r.item_id ? "…" : t("res.proofPdf")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
